// frontend/src/pages/HdFinishedGoodsPurchase.jsx
import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Button, SelectInput } from "../ui/UIComponents";

export default function HdFinishedGoodsPurchase() {
  const location = useLocation();
  // Mode selection & List management
  const [poList, setPoList] = useState([]);
  const [productList, setProductList] = useState([]);
  const [sellerOptions, setSellerOptions] = useState([]);
  const [editingPoId, setEditingPoId] = useState(null);
  const [loading, setLoading] = useState(false);

  // Header States
  const [seller, setSeller] = useState("");
  const [poDate, setPoDate] = useState(new Date().toISOString().split("T")[0]);
  const [poReference, setPoReference] = useState("");
  const [exportDocReq, setExportDocReq] = useState([]); 
  const [partialShipment, setPartialShipment] = useState("No");
  const [terms, setTerms] = useState("EXW");

  // Specialized Finished Goods Detail State
  const [lines, setLines] = useState([]);

  // Load prerequisites upon mounting workspace
  useEffect(() => {
    fetchSavedPOs();
    fetchSellerOptions();
    fetchProductCatalog();

    // Catch data packages pushed down from the cart module layout stream
    if (location.state?.incomingLines) {
      setLines(location.state.incomingLines);
    }
    if (location.state?.incomingSellerId) {
      // Auto-select master vendor relationship if matched
      setSeller(String(location.state.incomingSellerId));
    }
  }, []);

  const fetchSellerOptions = async () => {
    try {
      const res = await fetch("/api/data?scope=origin");
      if (res.ok) {
        const json = await res.json();
        setSellerOptions(json.rows || []);
      }
    } catch (err) {
      console.error("Error loading seller options:", err);
    }
  };

  const fetchProductCatalog = async () => {
    try {
      const res = await fetch("/api/data?scope=size&action=quick_products");
      if (res.ok) {
        const json = await res.json();
        setProductList(json.items || []);
      }
    } catch (err) {
      console.error("Error loading product catalog:", err);
    }
  };

  const fetchSavedPOs = async () => {
    try {
      const res = await fetch("/api/data?scope=purchase_order&action=list");
      if (res.ok) {
        const json = await res.json();
        setPoList(json.items || []);
      }
    } catch (err) {
      console.error("Error loading POs:", err);
    }
  };

  const handleLoadPO = async (poId) => {
    if (!poId) {
      resetForm();
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`/api/data?scope=purchase_order&action=get_fg&po_id=${poId}`);
      if (res.ok) {
        const json = await res.json();
        const data = json.data;
        if (data) {
          setEditingPoId(data.po_id);
          setSeller(data.seller || "");
          setPoDate(data.po_date ? data.po_date.split("T")[0] : "");
          setPoReference(data.po_reference || "");
          setExportDocReq(data.export_doc_req || []);
          setPartialShipment(data.partial_shipment || "No");
          setTerms(data.terms || "EXW");
          
          // Cast values cleanly to resolve float accumulation errors
          const formattedLines = (data.lines || []).map(line => ({
            ...line,
            line_seq: Number(line.line_seq),
            prod_id: Number(line.prod_id),
            width_mm: Number(line.width_mm) || 0,
            height_mm: Number(line.height_mm) || 0,
            qty: Number(line.qty) || 0,
            unit_price: Number(line.unit_price) || 0,
            line_total: Number(line.line_total) || 0,
            so_inv: Number(line.so_inv) || 0,
            config_snapshot: line.config_snapshot || null
          }));
          setLines(formattedLines);
        }
      }
    } catch (err) {
      alert("Failed to load Finished Goods PO details.");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEditingPoId(null);
    setSeller("");
    setPoDate(new Date().toISOString().split("T")[0]);
    setPoReference("");
    setExportDocReq([]);
    setPartialShipment("No");
    setTerms("EXW");
    setLines([]);
  };

  const handleLineFieldChange = (index, field, value) => {
    const updatedLines = [...lines];
    updatedLines[index][field] = value;

    if (field === "qty" || field === "unit_price") {
      const q = field === "qty" ? Number(value) : Number(updatedLines[index].qty);
      const p = field === "unit_price" ? Number(value) : Number(updatedLines[index].unit_price);
      updatedLines[index].line_total = q * p;
    }
    setLines(updatedLines);
  };

  const addLineItem = () => {
    const nextSeq = lines.length + 1;
    setLines([
      ...lines,
      {
        line_seq: nextSeq,
        prod_id: productList[0]?.prod_id || "",
        width_mm: 0,
        height_mm: 0,
        fab_collect: "",
        fab_color: "",
        prod_ops: "",
        qty: 1,
        unit_price: 0.0000,
        so_inv: 0.0000,
        line_total: 0.0000,
        notes: "",
        config_snapshot: null
      }
    ]);
  };

  const removeLineItem = (index) => {
    const remaining = lines.filter((_, i) => i !== index);
    const reSequenced = remaining.map((line, i) => ({
      ...line,
      line_seq: i + 1
    }));
    setLines(reSequenced);
  };

  const handleDocCheckboxChange = (docType) => {
    if (exportDocReq.includes(docType)) {
      setExportDocReq(exportDocReq.filter(item => item !== docType));
    } else {
      setExportDocReq([...exportDocReq, docType]);
    }
  };

  // Compiles the entire PO data state into a flat text ledger sheet
  const handleDownloadTxt = () => {
    if (lines.length === 0) {
      alert("No line items available to export.");
      return;
    }

    const padL = (str, len) => String(str || "").padStart(len);
    const padR = (str, len) => String(str || "").padEnd(len);
    const selectedSellerName = sellerOptions.find(o => String(o.opt_id) === String(seller))?.opt_name || "Unknown Vendor";
    
    const linesBuffer = [];
    const now = new Date();
    const dateStamp = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;

    // 1. Document Header Information Block
    linesBuffer.push("========================================================================================");
    linesBuffer.push("               FINISHED GOODS PURCHASE ORDER MANIFEST (XUẤT ĐƠN HÀNG THÀNH PHẨM)        ");
    linesBuffer.push("========================================================================================");
    linesBuffer.push(`PO Status     : ${editingPoId ? `EDITING PROFILE (ID: ${editingPoId})` : "NEW FRESH SHEET"}`);
    linesBuffer.push(`Export Date   : ${dateStamp} ${now.toTimeString().split(" ")[0]}`);
    linesBuffer.push(`Seller/Vendor : ${selectedSellerName} (Master Index ID: ${seller || "N/A"})`);
    linesBuffer.push(`PO Date Field : ${poDate}`);
    linesBuffer.push(`PO Reference  : ${poReference || "No Reference Logged"}`);
    linesBuffer.push(`Terms Profile : ${terms} | Partial Shipment Allowed: ${partialShipment}`);
    linesBuffer.push(`Export Doc Req: ${exportDocReq.length > 0 ? exportDocReq.join(", ") : "None Specified"}`);
    linesBuffer.push("========================================================================================");
    linesBuffer.push("");

    // 2. Tabular Column Matrix Definitions
    linesBuffer.push("STT | " + padR("Tên Sản Phẩm (Product Descriptor)", 30) + " | " + padL("Rộng(W)", 8) + " | " + padL("Cao(H)", 8) + " | " + padR("BST (Collection)", 16) + " | " + padR("Color", 12) + " | " + padL("SL/Qty", 4) + " | " + padL("ExW", 10) + " | " + padL("SO UP", 10) + " | " + padL("Sub total", 12));
    linesBuffer.push("---------------------------------------------------------------------------------------------------------------------------------------------");

    // 3. Matrix Mapping Grid Loop
    lines.forEach((line) => {
      const matchProduct = productList.find(p => Number(p.prod_id) === Number(line.prod_id));
      const productName = matchProduct ? `${matchProduct.pl} - ${matchProduct.desc}` : `Product ID: ${line.prod_id}`;
      
      linesBuffer.push(
        padR(String(line.line_seq), 3) + " | " +
        padR(productName.substring(0, 30), 30) + " | " +
        padL(`${line.width_mm}mm`, 8) + " | " +
        padL(`${line.height_mm}mm`, 8) + " | " +
        padR(String(line.fab_collect || "—").substring(0, 16), 16) + " | " +
        padR(String(line.fab_color || "—").substring(0, 12), 12) + " | " +
        padL(String(line.qty), 4) + " | " +
        padL(Number(line.unit_price).toFixed(2), 10) + " | " +
        padL(Number(line.so_inv || 0).toFixed(2), 10) + " | " +
        padL(Number(line.line_total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 }), 12)
      );

      // Extract and print granular manufacturing specs from the frozen config snapshot
      if (line.config_snapshot) {
        try {
          const cfg = typeof line.config_snapshot === "string" 
            ? JSON.parse(line.config_snapshot) 
            : line.config_snapshot;
          
          // Extract preformatted text configurations block
          if (cfg.snapshot) {
            const snapLines = String(cfg.snapshot).split("\n").map(l => l.trim()).filter(Boolean);
            if (snapLines.length > 0) {
              linesBuffer.push("    [Cấu hình sản phẩm / Detailed Configuration]:");
              snapLines.forEach(sl => linesBuffer.push(`      ${sl}`));
            }
          } 
          // Structural layout fallback if direct text snapshot isn't present
          else if (cfg.selections) {
            linesBuffer.push("    [Cấu hình sản phẩm / Detailed Configuration]:");
            const combinedVals = { ...(cfg.selections.reqValues || {}), ...(cfg.selections.opsValues || {}) };
            Object.entries(combinedVals).forEach(([keyId, valText]) => {
              if (valText) linesBuffer.push(`      - Option [${keyId}]: ${valText}`);
            });
          }
        } catch (err) {
          console.error("Error reading config snapshot payload summary text:", err);
        }
      }

      if (line.notes) {
        linesBuffer.push(`    ↳ Ghi chú/Notes: ${line.notes}`);
      }
      
      // Inject a blank line spacing break between multi-line records
      linesBuffer.push(""); 
    });

    linesBuffer.push("---------------------------------------------------------------------------------------------------------------------------------------------");
    linesBuffer.push(padL(`PO GRAND TOTAL VALUE: $${calculatedGrandTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })} USD`, 140));
    linesBuffer.push("========================================================================================");

    const fullTextBody = linesBuffer.join("\n");
    const sanitizedRef = (poReference || "NEW_PO").trim().replace(/\s+/g, "_");
    const compiledFileName = `PO_FINISHED_GOODS_${sanitizedRef}_${dateStamp}.txt`;

    // Dispatch parameters down into the active global window helper execution loop
    if (typeof window !== "undefined" && typeof window.dl_prod_ord_config_txt === "function") {
      window.dl_prod_ord_config_txt({ filename: compiledFileName, text: fullTextBody });
    } else {
      // Fallback baseline download block structure
      const blob = new Blob([fullTextBody], { type: "text/plain;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = compiledFileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  const handleSavePO = async (e) => {
    e.preventDefault();
    if (!seller) {
      alert("Please designate a Master Vendor choice.");
      return;
    }

    const payload = {
      po_id: editingPoId,
      seller,
      po_date: poDate,
      po_reference: poReference,
      export_doc_req: exportDocReq,
      partial_shipment: partialShipment,
      terms,
      lines
    };

    try {
      const res = await fetch("/api/data", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          scope: "purchase_order",
          action: "save_fg",
          ...payload
        })
      });

      if (res.ok) {
        alert(editingPoId ? "Finished Goods PO profile updated!" : "Finished Goods PO authorized successfully!");
        resetForm();
        fetchSavedPOs();
      } else {
        alert("Transaction submission rejected by core ledger rules.");
      }
    } catch (err) {
      console.error("Submission failed:", err);
    }
  };

  const calculatedGrandTotal = lines.reduce((acc, current) => acc + (Number(current.line_total) || 0), 0);

  return (
    <div className="app-page" style={{ maxWidth: "1400px", margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
        <h2>Purchase Department: Finished Goods Order Panel</h2>
        <div>
          <label style={{ marginRight: "8px", fontWeight: "600" }}>Review/Edit Order: </label>
          <select 
            className="text-input" 
            style={{ width: "240px", display: "inline-block" }}
            value={editingPoId || ""}
            onChange={(e) => handleLoadPO(e.target.value)}
          >
            <option value="">-- Create New Finished Goods PO --</option>
            {poList.map((po) => (
              <option key={po.po_id} value={po.po_id}>
                ID: {po.po_id} | Ref: {po.po_reference || "None"}
              </option>
            ))}
          </select>
          {editingPoId && (
            <Button onClick={resetForm} style={{ marginLeft: "10px", background: "#f0f0f0" }}>
              Clear / New
            </Button>
          )}
        </div>
      </div>

      {loading ? (
        <div style={{ padding: "40px", textAlign: "center" }}>Rehydrating finished goods ledger metrics...</div>
      ) : (
        <form onSubmit={handleSavePO}>
          {/* SECTION 1: HEADER CONTROLS */}
          <div className="page-section" style={{ marginBottom: "24px" }}>
            <h3 style={{ marginTop: 0, borderBottom: "1px solid #eee", paddingBottom: "8px" }}>
              {editingPoId ? `PO Header Profile — Editing Finished Goods ID: ${editingPoId}` : "PO Header Profile"}
            </h3>
            
            <div className="page-grid">
              <div>
                <label className="label">1. Seller *</label>
                <select className="text-input" value={seller} onChange={(e) => setSeller(e.target.value)} required>
                  <option value="">-- Select Master Vendor --</option>
                  {sellerOptions.map((opt) => (
                    <option key={opt.opt_id} value={opt.opt_id}>{opt.opt_name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="label">2. PO Date</label>
                <input type="date" className="text-input" value={poDate} onChange={(e) => setPoDate(e.target.value)} />
              </div>

              <div>
                <label className="label">3. PO Reference</label>
                <input type="text" className="text-input" value={poReference} onChange={(e) => setPoReference(e.target.value)} placeholder="User tracking string..." />
              </div>

              <div>
                <label className="label">5. Partial Shipment</label>
                <SelectInput value={partialShipment} onChange={(e) => setPartialShipment(e.target.value)} choices={["No", "Yes"]} />
              </div>

              <div>
                <label className="label">6. Terms</label>
                <SelectInput value={terms} onChange={(e) => setTerms(e.target.value)} choices={["EXW", "FOB", "CIF"]} />
              </div>

              <div>
                <label className="label">4. Export Doc Requirements</label>
                <div style={{ display: "flex", gap: "16px", marginTop: "10px" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                    <input type="checkbox" checked={exportDocReq.includes("CO")} onChange={() => handleDocCheckboxChange("CO")} /> CO
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                    <input type="checkbox" checked={exportDocReq.includes("CQ")} onChange={() => handleDocCheckboxChange("CQ")} /> CQ
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* SECTION 2: SPEC LIQUIDATION DETAILS MATRIX */}
          <div className="page-section" style={{ overflowX: "auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
              <h3 style={{ margin: 0 }}>Custom Manufactured Window Treatment Assembly List</h3>
              <div style={{ display: "flex", gap: "8px" }}>
                <Button 
                  type="button" 
                  onClick={() => window.open("https://web-advisor-dev.vercel.app/purchase/config/retail-product", "_blank")} 
                  style={{ background: "#fff", color: "#d32f2f", borderColor: "#d32f2f", fontWeight: "600" }}
                >
                  Check Item Code
                </Button>
                <Button type="button" onClick={addLineItem} style={{ background: "#eef9ff", color: "#0066cc", fontWeight: "600" }}>
                  + Append Product Row
                </Button>
              </div>
            </div>

            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px", minWidth: "1200px" }}>
              <thead>
                <tr style={{ background: "#f5f5f5", textAlign: "left", borderBottom: "2px solid #ddd" }}>
                  <th style={{ padding: "8px", width: "45px" }}>Seq</th>
                  <th style={{ padding: "8px", width: "180px" }}>Tên Sản Phẩm (Product)</th>
                  <th style={{ padding: "8px", width: "90px" }}>Rộng (W mm)</th>
                  <th style={{ padding: "8px", width: "90px" }}>Cao (H mm)</th>
                  <th style={{ padding: "8px", width: "130px" }}>Bộ sưu tập</th>
                  <th style={{ padding: "8px", width: "120px" }}>Màu sắc</th>
                  <th style={{ padding: "8px", width: "130px" }}>Vận hành</th>
                  <th style={{ padding: "8px", width: "75px" }}>SL (Qty)</th>
                  <th style={{ padding: "8px", width: "110px" }}>Đơn Giá (ExW)</th>
                  <th style={{ padding: "8px", width: "110px" }}>SO UP</th>
                  <th style={{ padding: "8px", width: "110px" }}>Thành Tiền</th>
                  <th style={{ padding: "8px" }}>Ghi chú (Notes)</th>
                  <th style={{ padding: "8px", width: "40px" }}></th>
                </tr>
              </thead>
              <tbody>
                {lines.length === 0 ? (
                  <tr>
                    <td colSpan="12" style={{ padding: "24px", textAlign: "center", color: "#888" }}>
                      No finished product lines loaded. Tap "+ Append Product Row" to populate order specs.
                    </td>
                  </tr>
                ) : (
                  lines.map((line, index) => (
                    <tr key={index} style={{ borderBottom: "1px solid #eee" }}>
                      <td style={{ padding: "6px", fontWeight: "bold" }}>{line.line_seq}</td>
                      <td style={{ padding: "6px" }}>
                        <select 
                          className="text-input" 
                          style={{ padding: "4px" }}
                          value={line.prod_id} 
                          onChange={(e) => handleLineFieldChange(index, "prod_id", e.target.value)}
                        >
                          {productList.map(p => (
                            <option key={p.prod_id} value={p.prod_id}>{p.pl} - {p.desc}</option>
                          ))}
                        </select>
                      </td>
                      <td style={{ padding: "6px" }}>
                        <input type="number" className="text-input" style={{ padding: "4px", textAlign: "right" }} value={line.width_mm} onChange={(e) => handleLineFieldChange(index, "width_mm", e.target.value)} />
                      </td>
                      <td style={{ padding: "6px" }}>
                        <input type="number" className="text-input" style={{ padding: "4px", textAlign: "right" }} value={line.height_mm} onChange={(e) => handleLineFieldChange(index, "height_mm", e.target.value)} />
                      </td>
                      <td style={{ padding: "6px" }}>
                        <input type="text" className="text-input" style={{ padding: "4px" }} value={line.fab_collect} onChange={(e) => handleLineFieldChange(index, "fab_collect", e.target.value)} placeholder="Collection..." />
                      </td>
                      <td style={{ padding: "6px" }}>
                        <input type="text" className="text-input" style={{ padding: "4px" }} value={line.fab_color} onChange={(e) => handleLineFieldChange(index, "fab_color", e.target.value)} placeholder="Color code..." />
                      </td>
                      <td style={{ padding: "6px" }}>
                        <input type="text" className="text-input" style={{ padding: "4px" }} value={line.prod_ops} onChange={(e) => handleLineFieldChange(index, "prod_ops", e.target.value)} placeholder="Operation profile..." />
                      </td>
                      <td style={{ padding: "6px" }}>
                        <input type="number" step="1" className="text-input" style={{ padding: "4px", textAlign: "right" }} value={line.qty} onChange={(e) => handleLineFieldChange(index, "qty", e.target.value)} />
                      </td>
                      <td style={{ padding: "6px" }}>
                        <input type="number" step="0.0001" className="text-input" style={{ padding: "4px", textAlign: "right" }} value={line.unit_price} onChange={(e) => handleLineFieldChange(index, "unit_price", e.target.value)} />
                      </td>
                      <td style={{ padding: "6px" }}>
                        <input 
                          type="number" 
                          step="0.0001" 
                          className="text-input" 
                          style={{ padding: "4px", textAlign: "right", background: "#fffdf0", borderColor: "#ffe5b0" }} 
                          value={line.so_inv} 
                          onChange={(e) => handleLineFieldChange(index, "so_inv", e.target.value)} 
                          placeholder="0.0000" 
                        />
                      </td>
                      <td style={{ padding: "6px", textAlign: "right", fontWeight: "600" }}>
                        {Number(line.line_total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </td>
                      <td style={{ padding: "6px" }}>
                        <input type="text" className="text-input" style={{ padding: "4px" }} value={line.notes} onChange={(e) => handleLineFieldChange(index, "notes", e.target.value)} />
                      </td>
                      <td style={{ padding: "6px", textAlign: "center" }}>
                        <button 
                          type="button" 
                          onClick={() => removeLineItem(index)}
                          style={{ background: "none", border: "none", color: "#cc0000", cursor: "pointer", fontSize: "16px" }}
                        >
                          &times;
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            {lines.length > 0 && (
              <div style={{ float: "right", marginTop: "16px", fontSize: "16px", fontWeight: "700" }}>
                Total EXW Accumulation Value: {calculatedGrandTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </div>
            )}
          </div>

          {/* MASTER CONTROLS */}
          <div style={{ marginTop: "20px", display: "flex", justifyContent: "flex-end", gap: "12px" }}>
            {lines.length > 0 && (
              <Button 
                type="button" 
                onClick={handleDownloadTxt} 
                style={{ background: "#fcfcfc", color: "#2e7d32", borderColor: "#2e7d32", marginRight: "auto", fontWeight: "700" }}
              >
                💾 Export Text Manifest (.TXT)
              </Button>
            )}
            <Button type="button" onClick={resetForm} style={{ background: "#fff", borderColor: "#ccc" }}>
              Reset Changes
            </Button>
            <Button type="submit" style={{ background: "#0088cc", color: "#fff", fontWeight: "700" }}>
              {editingPoId ? "Commit Finished Goods Updates" : "Authorize Finished Goods PO"}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}